import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Table, Button, Message, Checkbox, Popup, Input, Confirm } from 'semantic-ui-react';
import { isIntraLoggedIn, clearIntraLoggedIn } from './intraAuth';
import config from 'config/config';
import intraService from 'services/intraService';
import { AdminPerson, asBoolean, asString } from 'types/intra';

const foodPrice = Number(config.FOOD_PRICE);
const foodUnitPrice = Number.isFinite(foodPrice) ? foodPrice : 0;

function sortByName(a: AdminPerson, b: AdminPerson): number {
    const aFirst = (a.firstname ?? '').toLowerCase();
    const bFirst = (b.firstname ?? '').toLowerCase();
    const cmp = aFirst.localeCompare(bFirst, 'fi');
    if (cmp !== 0) return cmp;
    return (a.lastname ?? '').toLowerCase().localeCompare((b.lastname ?? '').toLowerCase(), 'fi');
}

const StaffPriceInput: React.FC<{
    person: AdminPerson;
    disabled: boolean;
    onSave: (price: number) => void;
}> = ({ person, disabled, onSave }) => {
    const stored = person.staff_price ?? 0;
    const [value, setValue] = useState(String(stored));

    useEffect(() => {
        setValue(String(stored));
    }, [stored]);

    const commit = () => {
        const parsed = Number.parseInt(value, 10);
        const next = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
        if (next === stored) {
            setValue(String(stored));
            return;
        }
        onSave(next);
    };

    return (
        <Input
            type="number"
            min={0}
            step={1}
            size="mini"
            value={value}
            disabled={disabled}
            onChange={(_, data) => setValue(String(data.value))}
            onBlur={commit}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                    e.currentTarget.blur();
                }
            }}
            input={{ style: { width: '4.5em' } }}
        />
    );
};

const AdminIntraView: React.FC = () => {
    const navigate = useNavigate();
    const [persons, setPersons] = useState<AdminPerson[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetting, setResetting] = useState(false);
    const resettingRef = useRef(false);

    const sortedPersons = useMemo(() => [...persons].sort(sortByName), [persons]);
    const totals = useMemo(() => {
        const counts = {
            food: 0,
            dietL: 0,
            dietG: 0,
            dietV: 0,
            dietOther: 0,
            arrived: 0,
            foodPaid: 0,
            staffFood: 0,
            staffPrice: 0
        };
        for (const person of persons) {
            if (asBoolean(person.food)) counts.food += 1;
            if (asBoolean(person.dietL)) counts.dietL += 1;
            if (asBoolean(person.dietG)) counts.dietG += 1;
            if (asBoolean(person.dietV)) counts.dietV += 1;
            if (asBoolean(person.dietOther)) counts.dietOther += 1;
            if ((person.arrived ?? 0) !== 0) counts.arrived += 1;
            if ((person.food_paid ?? 0) !== 0) counts.foodPaid += 1;
            if ((person.staff_food ?? 0) !== 0) counts.staffFood += 1;
            counts.staffPrice += person.staff_price ?? 0;
        }
        return counts;
    }, [persons]);

    useEffect(() => {
        if (!isIntraLoggedIn()) {
            navigate('/intra', { replace: true });
            return;
        }
        intraService
            .getAdminPersons()
            .then(setPersons)
            .catch((err: { message?: string }) => setError(err?.message ?? 'Henkilölistan haku epäonnistui.'))
            .finally(() => setLoading(false));
    }, [navigate]);

    const handleLogout = () => {
        clearIntraLoggedIn();
        navigate('/intra', { replace: true });
    };

    const handleResetConfirm = () => {
        if (resettingRef.current) return;
        resettingRef.current = true;
        setResetting(true);
        setError(null);
        intraService
            .resetAdmin()
            .then(() => intraService.getAdminPersons())
            .then(data => {
                setPersons(data);
                setResetOpen(false);
            })
            .catch((err: { message?: string }) => {
                setError(err?.message ?? 'Nollaus epäonnistui.');
                setResetOpen(false);
            })
            .finally(() => {
                resettingRef.current = false;
                setResetting(false);
            });
    };

    const arrivedBool = (p: AdminPerson) => (p.arrived ?? 0) !== 0;
    const foodPaidBool = (p: AdminPerson) => (p.food_paid ?? 0) !== 0;
    const staffFoodBool = (p: AdminPerson) => (p.staff_food ?? 0) !== 0;
    const staffPrice = (p: AdminPerson) => p.staff_price ?? 0;

    const updateAttendance = (
        p: AdminPerson,
        arrived: boolean,
        food_paid: boolean,
        staff_food: boolean,
        staff_price: number
    ) => {
        const prevState = {
            arrived: p.arrived ?? 0,
            food_paid: p.food_paid ?? 0,
            staff_food: p.staff_food ?? 0,
            staff_price: p.staff_price ?? 0
        };
        setUpdatingId(p.id);
        setPersons(prev =>
            prev.map(x =>
                x.id === p.id
                    ? {
                          ...x,
                          arrived: arrived ? 1 : 0,
                          food_paid: food_paid ? 1 : 0,
                          staff_food: staff_food ? 1 : 0,
                          staff_price
                      }
                    : x
            )
        );
        intraService
            .updateArrivedAndFoodPaid(p.id, arrived, food_paid, staff_food, staff_price)
            .catch(() => setPersons(prev => prev.map(x => (x.id === p.id ? { ...x, ...prevState } : x))))
            .finally(() => setUpdatingId(null));
    };

    const handleArrivedChange = (p: AdminPerson) => {
        updateAttendance(p, !arrivedBool(p), foodPaidBool(p), staffFoodBool(p), staffPrice(p));
    };

    const handleFoodPaidChange = (p: AdminPerson) => {
        updateAttendance(p, arrivedBool(p), !foodPaidBool(p), staffFoodBool(p), staffPrice(p));
    };

    const handleStaffFoodChange = (p: AdminPerson) => {
        updateAttendance(p, arrivedBool(p), foodPaidBool(p), !staffFoodBool(p), staffPrice(p));
    };

    const handleStaffPriceChange = (p: AdminPerson, price: number) => {
        updateAttendance(p, arrivedBool(p), foodPaidBool(p), staffFoodBool(p), price);
    };

    if (!isIntraLoggedIn()) {
        return null;
    }

    return (
        <Container>
            <Header as="h2">
                AsLAN Intra – Admin
                <Button floated="right" size="small" onClick={handleLogout}>
                    Kirjaudu ulos
                </Button>
            </Header>
            <div style={{ clear: 'both', marginBottom: '1em' }}>
                <Button negative size="small" disabled={loading || resetting} onClick={() => setResetOpen(true)}>
                    Nollaa ilmoittautumiset
                </Button>
            </div>
            <Confirm
                open={resetOpen}
                size="small"
                header="Oletko aivan varma?"
                content="Tämä tyhjentää *KAIKKI* ilmoittautumiset ja paikkavaraukset!"
                cancelButton={{ content: 'Peruuta', disabled: resetting }}
                confirmButton={{ content: 'Tyhjennä', negative: true, loading: resetting, disabled: resetting }}
                onCancel={() => {
                    if (!resetting) setResetOpen(false);
                }}
                onConfirm={handleResetConfirm}
            />
            {error && <Message negative>{error}</Message>}
            {loading ? (
                <p>Ladataan…</p>
            ) : (
                <Table celled stackable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={3}>Nimi</Table.HeaderCell>
                            <Table.HeaderCell width={3}>Puhelin</Table.HeaderCell>
                            <Table.HeaderCell width={1}>To&nbsp;/&nbsp;Pe</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Sijainti</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Ruoka</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Lakton</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Gluton</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Vege</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Muu</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Saapui</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Ruoka-maksu</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Staff-ruoka</Table.HeaderCell>
                            <Table.HeaderCell width={2}>Staff-hinta €</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {sortedPersons.map(p => (
                            <Table.Row key={p.id}>
                                <Table.Cell>
                                    <Popup
                                        content={`ID: ${asString(p.id)}`}
                                        trigger={
                                            <span>
                                                {asString(p.firstname)} {asString(p.lastname)}
                                            </span>
                                        }
                                    />
                                </Table.Cell>
                                <Table.Cell>{asString(p.phone)}</Table.Cell>
                                <Table.Cell>
                                    {asBoolean(p.attendingThu) ? 'To ' : ''}
                                    {asBoolean(p.attendingFri) ? ' Pe' : ''}
                                </Table.Cell>
                                <Table.Cell>{asString(p.Location)}</Table.Cell>
                                <Table.Cell>{asBoolean(p.food) ? 'Kyllä' : ''}</Table.Cell>
                                <Table.Cell>{asBoolean(p.dietL) ? 'Kyllä' : ''}</Table.Cell>
                                <Table.Cell>{asBoolean(p.dietG) ? 'Kyllä' : ''}</Table.Cell>
                                <Table.Cell>{asBoolean(p.dietV) ? 'Kyllä' : ''}</Table.Cell>
                                <Table.Cell>
                                    {asBoolean(p.dietOther) ? (
                                        <Popup content={asString(p.dietOtherComment)} trigger={<span>Kyllä</span>} />
                                    ) : (
                                        ''
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <Checkbox
                                        checked={arrivedBool(p)}
                                        disabled={updatingId === p.id}
                                        onChange={() => handleArrivedChange(p)}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    <Checkbox
                                        checked={foodPaidBool(p)}
                                        disabled={updatingId === p.id}
                                        onChange={() => handleFoodPaidChange(p)}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    <Checkbox
                                        checked={staffFoodBool(p)}
                                        disabled={updatingId === p.id}
                                        onChange={() => handleStaffFoodChange(p)}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    <StaffPriceInput
                                        person={p}
                                        disabled={updatingId === p.id}
                                        onSave={price => handleStaffPriceChange(p, price)}
                                    />
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                    <Table.Footer>
                        <Table.Row>
                            <Table.HeaderCell>Yhteensä: {persons.length}</Table.HeaderCell>
                            <Table.HeaderCell colSpan={3} />
                            <Table.HeaderCell>{totals.food}</Table.HeaderCell>
                            <Table.HeaderCell>{totals.dietL}</Table.HeaderCell>
                            <Table.HeaderCell>{totals.dietG}</Table.HeaderCell>
                            <Table.HeaderCell>{totals.dietV}</Table.HeaderCell>
                            <Table.HeaderCell>{totals.dietOther}</Table.HeaderCell>
                            <Table.HeaderCell>{totals.arrived}</Table.HeaderCell>
                            <Table.HeaderCell>
                                {totals.foodPaid * foodUnitPrice} € ({totals.foodPaid})
                            </Table.HeaderCell>
                            <Table.HeaderCell>{totals.staffFood}</Table.HeaderCell>
                            <Table.HeaderCell>{totals.staffPrice} €</Table.HeaderCell>
                        </Table.Row>
                    </Table.Footer>
                </Table>
            )}
        </Container>
    );
};

export default AdminIntraView;
